// /tenant/lease
import { useEffect, useState } from "react";
import api from "@/lib/api";
import TenantShell from "@/components/TenantShell";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Download, FileText, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

function Pill({ status }) {
  const map = {
    active: "bg-emerald-100 text-emerald-700",
    ending_soon: "bg-amber-100 text-amber-700",
    draft: "bg-slate-100 text-slate-600",
    expired: "bg-slate-100 text-slate-500",
    terminated: "bg-red-100 text-red-700",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map[status] || "bg-slate-100"}`}>
      {(status || "").replace("_", " ")}
    </span>
  );
}

export default function TenantLease() {
  const [data, setData] = useState(null);
  const [renewalOpen, setRenewalOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [extMonths, setExtMonths] = useState(12);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/tenant/lease").then(({ data }) => setData(data));
  }, []);

  const download = async () => {
    try {
      const { data } = await api.get("/tenant/lease/document/url");
      const a = document.createElement("a");
      a.href = data.url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = data.filename || "lease.pdf";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No lease document on file");
    }
  };

  const submitRenewal = async () => {
    setSaving(true);
    try {
      await api.post("/tenant/lease/renewal-request", { notes, preferred_extension_months: Number(extMonths) || 12 });
      toast.success("Renewal request sent");
      setRenewalOpen(false);
      setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send request");
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <TenantShell><div className="flex h-64 items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin" /></div></TenantShell>;
  }

  const { lease, property, past_leases } = data;

  return (
    <TenantShell>
      <div className="space-y-6" data-testid="tenant-lease-page">
        <header>
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-600">Lease</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">My Lease</h1>
        </header>

        {!lease ? (
          <p className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="tenant-lease-empty">No active lease.</p>
        ) : (
          <>
            <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="tenant-lease-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-lg font-semibold text-slate-900">{lease.start_date} – {lease.end_date}</p>
                  <p className="text-sm text-slate-500">Term: {lease.term_months} months</p>
                </div>
                <Pill status={lease.status} />
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-3 text-sm sm:grid-cols-3">
                <div><dt className="text-[10px] uppercase tracking-wider text-slate-500">Monthly rent</dt><dd className="font-semibold tabular-nums text-slate-900">{lease.monthly_rent} {lease.currency}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wider text-slate-500">Security deposit</dt><dd className="font-medium tabular-nums text-slate-900">{lease.security_deposit} {lease.currency}</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wider text-slate-500">Late fee</dt><dd className="text-slate-900">{lease.late_fee_amount} {lease.currency} after {lease.late_fee_grace_days}d</dd></div>
                <div><dt className="text-[10px] uppercase tracking-wider text-slate-500">Pets</dt><dd className="text-slate-900">{lease.pets_allowed ? "Allowed" : "Not allowed"}</dd></div>
                <div className="col-span-2 sm:col-span-2"><dt className="text-[10px] uppercase tracking-wider text-slate-500">Utilities included</dt><dd className="mt-1 flex flex-wrap gap-1">{(lease.utilities_included || []).length === 0 ? <span className="text-xs text-slate-400">None</span> : (lease.utilities_included || []).map(u => <span key={u} className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-semibold text-indigo-700">{u}</span>)}</dd></div>
              </dl>
              {lease.notes && <p className="mt-4 rounded-md bg-slate-50 p-3 text-xs text-slate-600">{lease.notes}</p>}
              <div className="mt-5 flex flex-wrap gap-2">
                {lease.document_key && (
                  <Button variant="outline" onClick={download} data-testid="tenant-lease-download">
                    <Download className="mr-2 h-4 w-4" /> Download lease PDF
                  </Button>
                )}
                {lease.status !== "expired" && lease.status !== "terminated" && (
                  <Button onClick={() => setRenewalOpen(true)} className="bg-indigo-600 text-white hover:bg-indigo-700" data-testid="tenant-renewal-button">
                    <RefreshCw className="mr-2 h-4 w-4" /> Request renewal
                  </Button>
                )}
              </div>
            </section>

            {property && (
              <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="tenant-property-card">
                <h2 className="text-sm font-semibold uppercase tracking-[0.14em] text-slate-500">Property details</h2>
                <p className="mt-3 text-base font-semibold text-slate-900">{property.address}</p>
                <p className="text-sm text-slate-500">{property.city}, {property.state || ""} {property.country}</p>
                <dl className="mt-3 grid grid-cols-3 gap-3 text-xs text-slate-700 sm:grid-cols-5">
                  <div><dt className="text-[9px] uppercase text-slate-400">Beds</dt><dd>{property.beds || "—"}</dd></div>
                  <div><dt className="text-[9px] uppercase text-slate-400">Baths</dt><dd>{property.baths || "—"}</dd></div>
                  <div><dt className="text-[9px] uppercase text-slate-400">Sqft</dt><dd>{property.square_feet || "—"}</dd></div>
                  <div><dt className="text-[9px] uppercase text-slate-400">Type</dt><dd>{property.property_type || "—"}</dd></div>
                  <div><dt className="text-[9px] uppercase text-slate-400">Year</dt><dd>{property.year_built || "—"}</dd></div>
                </dl>
              </section>
            )}

            {past_leases && past_leases.length > 0 && (
              <section>
                <details className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
                  <summary className="cursor-pointer text-sm font-semibold text-slate-700">Past leases ({past_leases.length})</summary>
                  <ul className="mt-3 space-y-2">
                    {past_leases.map((l) => (
                      <li key={l.id} className="flex items-center justify-between text-xs">
                        <span>{l.start_date} – {l.end_date}</span>
                        <Pill status={l.status} />
                      </li>
                    ))}
                  </ul>
                </details>
              </section>
            )}
          </>
        )}
      </div>

      <Dialog open={renewalOpen} onOpenChange={setRenewalOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white" data-testid="tenant-renewal-modal">
          <DialogHeader>
            <DialogTitle>Request lease renewal</DialogTitle>
            <DialogDescription className="text-slate-500">Send a renewal signal to your landlord. They'll respond with terms.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="ext">Preferred extension (months)</Label>
              <Input id="ext" type="number" min={1} max={60} value={extMonths} onChange={(e) => setExtMonths(e.target.value)} data-testid="tenant-renewal-months" />
            </div>
            <div>
              <Label htmlFor="rnotes">Notes (optional)</Label>
              <Textarea id="rnotes" rows={4} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything you'd like the landlord to know…" data-testid="tenant-renewal-notes" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={() => setRenewalOpen(false)} disabled={saving}>Cancel</Button>
              <Button onClick={submitRenewal} className="bg-indigo-600 text-white hover:bg-indigo-700" disabled={saving} data-testid="tenant-renewal-submit">
                {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />}
                Send request
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </TenantShell>
  );
}
